import { formatPrice, SelectedPlan } from '../data/pricingPlans'

export type TermsHighlight = {
  title: string
  detail: string
}

const baseHighlights: TermsHighlight[] = [
  {
    title: 'First-day trial',
    detail:
      'Your free trial starts on the first day you sign up. You have full access from day one and will not be charged during the trial.',
  },
  {
    title: '12-month contract period',
    detail:
      'When the trial ends, your subscription runs for a minimum contract period of 12 months, billed monthly by Direct Debit.',
  },
]

export function termsHighlightsFor(plan?: SelectedPlan | null) {
  if (!plan) return baseHighlights
  return [
    ...baseHighlights,
    {
      title: 'Your plan',
      detail: plan.discountLabel
        ? `${plan.name} at ${formatPrice(plan.price)} per month, including your ${plan.discountLabel} ${plan.discountPercent}% discount off the standard ${formatPrice(plan.standardPrice)} price.`
        : `${plan.name} at ${formatPrice(plan.price)} per month, charged after your free trial ends.`,
    },
  ]
}

type PdfLine = {
  text: string
  bold?: boolean
  size?: number
  spaceAfter?: number
}

function planLines(plan?: SelectedPlan | null): PdfLine[] {
  if (!plan) return []
  const lines: PdfLine[] = [
    { text: 'Your selected plan', bold: true, size: 12, spaceAfter: 8 },
    {
      text: `Plan: ${plan.name}`,
      spaceAfter: 4,
    },
    {
      text: `Price after free trial: ${formatPrice(plan.price)} per month`,
      spaceAfter: 4,
    },
  ]
  if (plan.discountLabel) {
    lines.push({
      text: `Includes ${plan.discountLabel} discount of ${plan.discountPercent}% off ${formatPrice(plan.standardPrice)}.`,
      spaceAfter: 4,
    })
  }
  lines.push({ text: '', spaceAfter: 16 })
  return lines
}

const documentLines: PdfLine[] = [
  { text: 'Credit Manager', bold: true, size: 20, spaceAfter: 10 },
  { text: 'Terms and Conditions', bold: true, size: 14, spaceAfter: 24 },
  { text: '1. First-day trial', bold: true, size: 12, spaceAfter: 8 },
  {
    text: 'Your free trial begins on the first day you sign up for Credit Manager.',
    spaceAfter: 4,
  },
  {
    text: 'You have full access to credit checks, invoice chasing and finance',
    spaceAfter: 4,
  },
  {
    text: 'features from day one, and no payment is taken during the trial period.',
    spaceAfter: 20,
  },
  { text: '2. 12-month contract period', bold: true, size: 12, spaceAfter: 8 },
  {
    text: 'At the end of the free trial, your subscription continues for a minimum',
    spaceAfter: 4,
  },
  {
    text: 'contract period of 12 months. Fees are collected monthly by Direct Debit',
    spaceAfter: 4,
  },
  {
    text: 'through GoCardless, starting on the first day after the trial ends.',
    spaceAfter: 20,
  },
  { text: '3. Billing and cancellation', bold: true, size: 12, spaceAfter: 8 },
  {
    text: 'You may cancel at any time during the free trial at no cost. After the',
    spaceAfter: 4,
  },
  {
    text: 'contract period begins, cancellation takes effect at the end of the',
    spaceAfter: 4,
  },
  { text: '12-month term unless otherwise agreed in writing.', spaceAfter: 20 },
  { text: '4. Your data', bold: true, size: 12, spaceAfter: 8 },
  {
    text: 'Company and contact details are used to create and operate your account.',
    spaceAfter: 4,
  },
  {
    text: 'Accounting software connections are made securely through Satago and can',
    spaceAfter: 4,
  },
  { text: 'be revoked by you at any time.', spaceAfter: 20 },
  { text: '5. Support', bold: true, size: 12, spaceAfter: 8 },
  {
    text: 'For questions about these terms, contact support@creditmanager.co.uk.',
    spaceAfter: 4,
  },
]

function escapePdfText(value: string) {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)')
}

function buildContentStream(lines: PdfLine[]) {
  let cursor = 780
  const parts: string[] = []
  lines.forEach((line) => {
    const size = line.size || 11
    const font = line.bold ? '/F2' : '/F1'
    parts.push(
      `BT\n${font} ${size} Tf\n1 0 0 1 56 ${cursor} Tm\n(${escapePdfText(line.text)}) Tj\nET`,
    )
    cursor -= size + (line.spaceAfter === undefined ? 6 : line.spaceAfter)
  })
  return parts.join('\n')
}

function buildTermsPdf(plan?: SelectedPlan | null) {
  const content = buildContentStream([
    ...documentLines.slice(0, 2),
    ...planLines(plan),
    ...documentLines.slice(2),
  ])
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R /F2 5 0 R >> >> /Contents 6 0 R >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>',
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
  ]

  let pdf = '%PDF-1.4\n'
  const offsets: number[] = []
  objects.forEach((body, index) => {
    offsets.push(pdf.length)
    pdf += `${index + 1} 0 obj\n${body}\nendobj\n`
  })

  const xrefOffset = pdf.length
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`
  offsets.forEach((offset) => {
    pdf += `${offset.toString().padStart(10, '0')} 00000 n \n`
  })
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

  return pdf
}

export function downloadTermsPdf(plan?: SelectedPlan | null) {
  const blob = new Blob([buildTermsPdf(plan)], {
    type: 'application/pdf',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'credit-manager-terms-and-conditions.pdf'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}
